import router from "next/router";
import { removeCookie } from "./cookie";
import { getAccessToken } from "./tokenStore";
import { clearLegacyToken } from "./legacyToken";
import axios from "axios";
import { resetMixpanel } from "./mixPanel";
import { clearSentryUser } from "./sentryUser";
import clearLocalSession, {
  getPushSubscription,
  isImpersonatingSession,
} from "./clearLocalSession";
const URL_API = process.env.URL_API;
const logoutURL = `${URL_API}/identity/suppliers/logout`;

/**
 * Clears the local session, then redirects.
 *
 * This used to bail out with `if (!getCookie("auth")) return false` before
 * doing anything, which skipped both the cleanup and the redirect in exactly
 * the broken state that needed them most: a partial session holding `token`
 * but no `auth` (#1040). The stale cookies then survived every 401, so the
 * /sign-in <-> /dashboard redirect loop could never clear itself. Local
 * cleanup and the redirect now always run.
 *
 * sellsmart-api#4626 AC-1.10: two kinds of sign-out.
 * - `revoke: true` (logout, leave impersonation, forgot password, the idle
 *   timeout): the API logout is called with credentials, which revokes the
 *   HttpOnly refresh cookie, so a reload cannot restore the session.
 * - default, local only (a refused refresh, a 401 from an API call): this
 *   tab lost its session; nothing is sent to the API.
 * `navigate: false` clears local state but stays on the current route.
 */
const logoutAndRedirect = async (
  redirect_url: string,
  { revoke = false, navigate = true }: { revoke?: boolean; navigate?: boolean } = {},
) => {
  const token = getAccessToken();
  // Read before removeItems() deletes the cookie it comes from (#4334 S6.3).
  const impersonating = isImpersonatingSession();

  try {
    if (revoke) {
      // This device's push endpoint, so the API deletes only its row and the
      // supplier's other devices keep theirs (#4334 S7, ADR-9). Never sent
      // for an impersonation (N-56); undefined when push is off (N-55).
      const pushEndpoint = impersonating
        ? undefined
        : (await getPushSubscription())?.endpoint;
      await axios.post(
        logoutURL,
        { pushEndpoint },
        {
          // The cookie alone can end the session, so no token is needed. With
          // a token the API also blacklists it.
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
    }
  } catch (error) {
    // Swallowed on purpose: a failed (or impossible) server logout must not
    // stop us clearing local state, or the browser keeps a broken session.
  }

  removeItems();
  await clearLocalSession({ impersonating });
  resetMixpanel();
  clearSentryUser();

  // Awaited, so a caller that renders after this resolves is already on the
  // new route. `navigate: false` keeps the current route (and its query).
  if (navigate) {
    await router.push(redirect_url);
  }

  return true;
};

const removeItems = () => {
  removeCookie("authenticated");
  removeCookie("auth");
  clearLegacyToken();
  removeCookie("authVerified");
  removeCookie("lastActive");
  removeCookie("defaultQuoteExpiry");
  removeCookie("businessProfileID");
  removeCookie("emailVerified");
  removeCookie("inaccessibleurl");
  removeCookie("bankAccountID");
  removeCookie("canManageBanks");
  removeCookie("quoteSetting");
  removeCookie("lastActive");
  removeCookie("isUserImpersonate");
  removeCookie("termsAccepted");
  removeCookie("role");
  removeCookie("categories");
  removeCookie("permissions");
  // Logging out must drop keepLoggedIn too. While it survived a logout the
  // sign-in page kept skipping its clearAllCookies() safety net (#1040), so
  // a half-cleared session stayed half-cleared for the life of the browser.
  removeCookie("keepLoggedIn");
};

export default logoutAndRedirect;
